import SharedDialog from '../../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addShortcutToDrive, UserKeys } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { Doc, Folder, SharedDriveModel } from '../../../../../types';
import { useState } from 'react';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import SharedDrivesPermissionCheckDialog from './shared-drives/shared-drives-parent/shared-drives-permission-check-dialog';
import { CallbackDoc } from 'react-google-drive-picker/dist/typeDefs';
import DriveListDialogContent from './drive-list-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';

import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';

interface DriveAddShortcutDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  clickedDoc: Doc;
  driveId: string;
  changeLogRoles: string[];
  isAllUsers?: boolean;
}

export default function DriveAddShortcutDialog({
  open,
  handleClose,
  userEmail,
  clickedDoc,
  driveId,
  changeLogRoles,
  isAllUsers,
}: DriveAddShortcutDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [folder, setFolder] = useState(driveId);
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openPermissionCheckDialog, setOpenPermissionCheckDialog] =
    useState(false);
  const [selectedTab, setSelectedTab] = useState('drive');
  const [permissionCheckLoading, setPermissionCheckLoading] = useState(false);
  const [autocompleteValue, setAutocompleteValue] = useState('');
  const [selectedDrivePickerFolder, setSelectedDrivePickerFolder] =
    useState<CallbackDoc | null>(null);

  const isSharedDriveFolder = selectedTab === 'sharedDrives';

  const [selectedDrive, setSelectedDrive] = useState<SharedDriveModel | null>(
    null
  );

  const { mutate, isPending: isAddingShortcut } = useMutation({
    mutationFn: (destinationId: string) =>
      addShortcutToDrive(
        userEmail,
        clickedDoc.id,
        destinationId,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Shortcut for "${clickedDoc.title}" added successfully to ${selectedFolderTitle}.`,
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveDocuments, userEmail, folder],
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedDriveDocuments, userEmail, folder],
      });

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue adding the shortcut. Please refresh and try again.',
      });
    },
    onSettled: () => {
      if (isAllUsers) {
        createEvent(analytics.drive.drive_explorer.add_shortcut, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(analytics.user_management.user_details.drive.add_shortcut, {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.drive,
        });
      }
      setOpenChangeLogDialog(false);
    },
  });

  const selectedFolderDetails = { title: 'the selected folder' } as Folder;

  const selectedFolderTitle = (() => {
    if (isSharedDriveFolder) {
      return (
        selectedDrivePickerFolder?.name ||
        selectedDrive?.name ||
        'the selected folder'
      );
    }
    return folder === driveId
      ? 'My Drive'
      : selectedFolderDetails?.title || 'the selected folder';
  })();

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanAddShortcuts.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const destinationId = isSharedDriveFolder
        ? selectedDrivePickerFolder?.id || selectedDrive?.id || ''
        : folder;
      mutate(destinationId);
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={'Add shortcut'}
      actingAs={userEmail}
      dialogContent={
        <Box sx={{ minHeight: 250 }}>
          <Typography>
            Add shortcut for <strong>{clickedDoc.title}</strong>. Select the
            folder you'd like to add the shortcut to.
          </Typography>
          <Tabs
            value={selectedTab}
            onChange={(_event, newValue) => setSelectedTab(newValue)}
          >
            <Tab value="drive" label="My Drive" />
            <Tab value="sharedDrives" label="Shared Drive" />
          </Tabs>
          <DriveListDialogContent
            selectedTab={selectedTab}
            userEmail={userEmail}
            driveId={driveId}
            folder={folder}
            setFolder={setFolder}
            selectedDocs={[clickedDoc]}
            autocompleteValue={autocompleteValue}
            setAutocompleteValue={setAutocompleteValue}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            selectedDrivePickerFolder={selectedDrivePickerFolder}
            setSelectedDrivePickerFolder={setSelectedDrivePickerFolder}
            selectedPickerFolderIsInDocs={false}
            openChangeLogDialog={openChangeLogDialog}
            setOpenChangeLogDialog={setOpenChangeLogDialog}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            isMoving={isAddingShortcut}
            openPermissionCheckDialog={openPermissionCheckDialog}
            setOpenPermissionCheckDialog={setOpenPermissionCheckDialog}
            changeLogRoles={changeLogRoles}
            setPermissionCheckLoading={setPermissionCheckLoading}
          />

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const destinationId = isSharedDriveFolder
                ? selectedDrivePickerFolder?.id || selectedDrive?.id || ''
                : folder;

              mutate(destinationId);
            }}
            mutateLoading={isAddingShortcut}
          />
          {selectedDrive && openPermissionCheckDialog ? (
            <SharedDrivesPermissionCheckDialog
              open={openPermissionCheckDialog}
              handleClose={() => {
                setOpenPermissionCheckDialog(false);
                setSelectedDrive(null);
              }}
              minimumRequiredRole="reader"
              folderOrDriveId={selectedDrive.id}
              actionAfterPermissionGranted={() => {
                setFolder(selectedDrive.id);
                setSelectedDrive(null);
              }}
              nameOfSharedDrive={selectedDrive.name}
            />
          ) : null}
        </Box>
      }
      primaryButton={{
        title: 'Add Shortcut',
        action: handleSubmit,
        loading: isAddingShortcut || permissionCheckLoading,
        disabled: isAddingShortcut || permissionCheckLoading,
      }}
    />
  );
}
